import { useCallback, useSyncExternalStore } from "react";

/**
 * The first corner alert of a browser comes with a floating card that explains them: what they are for, how
 * each section sounds, and where to change them. Shown once, from the moment an alert first shows until the
 * person closes it or goes to the settings; a reload before that shows it again with the next alert.
 */
const SEEN_KEY = "coloca:dashboard-alerts-intro-seen";

function wasSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    // Without storage it shows once per tab, which is still once per visit.
    return seenHere;
  }
}

let seenHere = false;
let visible = false;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Called by the presenter each time an alert shows: the first one, ever, brings the card up. */
export function noteDashboardAlertShown(): void {
  if (visible || seenHere || wasSeen()) return;
  visible = true;
  notify();
}

/** Closes the card for good, in this browser. */
export function dismissDashboardAlertIntro(): void {
  visible = false;
  seenHere = true;
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    // Remembered for this tab only.
  }
  notify();
}

export function useDashboardAlertIntro() {
  const open = useSyncExternalStore(
    subscribe,
    () => visible,
    () => false,
  );
  const dismiss = useCallback(() => dismissDashboardAlertIntro(), []);
  return { open, dismiss };
}
